<template>
  <v-dialog v-model="openDialog"
            lazy
            @input="v => v"
            :full-width="!$vuetify.breakpoint.smAndDown"
            max-width="500">
    <v-card class="pa-4">
      <v-toolbar>
        <v-toolbar-side-icon>
          <v-icon color="accent">headset_mic</v-icon>
        </v-toolbar-side-icon>
        <v-layout column>
          <v-toolbar-title v-if="outsideTrucks">Record Phone Dispatch</v-toolbar-title>
          <v-toolbar-title v-else>Assign to {{loadGroupOwnerName(load)}}</v-toolbar-title>
          <v-list-tile-sub-title class="font-italic">
            Assignable loads: {{planningLoadGroupAvailableTruckCount(load)}}
          </v-list-tile-sub-title>
        </v-layout>
      </v-toolbar>
      <v-layout column pt-3>
        <span class="title mb-2">Trucks</span>
        <v-select
          @change="$event > 1 ? assignTruckDriverUserId = null : 1"
          :items="planningLoadGroupAvailableLoadAssignmentRangeOptions(load)"
          v-model.number="assignTruckCount"
          :error-messages="errors.first('assignedLoads', 'modal')"
          data-vv-as="Assigned Truck Count"
          data-vv-name="planningLoad.__assignTruckCount"
          data-vv-scope="modal"
          v-validate="`required|min_value:1|max_value:${planningLoadGroupAvailableTruckCount(load)}`"
          label="Assigned Truck Count">
        </v-select>

        <template v-if="outsideTrucks">
          <v-subheader class="pl-0"
                       :disabled="!canAssignDriverToDirectDispatchLoads">
            Notify the office of the outside truck assignment:
          </v-subheader>

          <v-list two-line>
            <v-list-tile>
              <v-list-tile-action><v-icon color="primary">headset_mic</v-icon></v-list-tile-action>
              <v-autocomplete
                class="mt-0"
                clearable
                label="Contact Dispatcher"
                :loading="false"
                :value="value"
                :disabled="false"
                :items="company.dispatchContacts || []"
                :search-input.sync="search"
                v-validate.immediate="{ required: true }"
                :error-messages="errors.first('dispatchContactId', 'modal')"
                data-vv-as="Dispatch Contact"
                data-vv-name="dispatchContactId"
                data-vv-scope="modal"
                item-text="name"
                return-object
                v-model="assignedDispatchContact">
                <template slot="item" slot-scope="data">
                  <v-layout column>
                    <v-list-tile-title>{{ data.item.name }}</v-list-tile-title>
                    <v-list-tile-sub-title>
                      <v-icon class="pl-1 pr-2" small>phonelink_ring</v-icon>{{ data.item.phone }}
                    </v-list-tile-sub-title>
                  </v-layout>
                </template>
              </v-autocomplete>
            </v-list-tile>
            <v-list-tile>
              <v-list-tile-content class="checkbox-content">
                <v-layout row>
                  <v-icon v-if="!outsideTrucksPercentage">attach_money</v-icon>

                  <v-text-field label="Offer Rate"
                                v-model="outsideTrucksRate"
                                :error-messages="errors.first('offerRate', 'modal')"
                                data-vv-as="Load Offer Rate"
                                data-vv-name="offerRate"
                                data-vv-scope="modal"
                                :class="{'pb-2': errors.any()}"
                                v-validate.immediate="{ required: true }">
                  </v-text-field>
                  <v-icon v-if="outsideTrucksPercentage">percent</v-icon>
                </v-layout>
              </v-list-tile-content>
              <v-list-tile-action>
                <v-checkbox
                  label="as a percentage"
                  v-model="outsideTrucksPercentage"
                />
              </v-list-tile-action>
            </v-list-tile>
          </v-list>
          <v-text-field label="Notes"
                        v-model="outsideTrucksNote">
          </v-text-field>
        </template>
        <template v-else>
          <v-subheader class="pl-0"
                       :disabled="!canAssignDriverToDirectDispatchLoads">
            Optionally, direct dispatch a driver:
          </v-subheader>
          <v-select-driver :company="company"
                           :disabled="!canAssignDriverToDirectDispatchLoads"
                           :value="assignTruckDriverUserId"
                           @change="selectDriverChange($event)">
          </v-select-driver>
        </template>

        <v-card-actions>
          <v-spacer />
          <v-btn color="accent"
                 :loading="loadingBtn"
                 :disabled="errors.any('modal')"
                 @click="assignTrucks()">
            Assign
          </v-btn>
          <v-btn color="accent" flat
                 @click="openDialog = false">
            Cancel
          </v-btn>
        </v-card-actions>
      </v-layout>
    </v-card>
  </v-dialog>
</template>

<script>
import API from '@/api';
import * as types from '@/store/mutationTypes';
import bookedLoadMixin from '@/mixins/bookedLoadMixin';
import planningLoadGroupsMixin from '@/mixins/planningLoadGroupsMixin';
import VSelectDriver from '@/components/SelectDriver';
import BqaCertificateIcon from '../BqaCertificateIcon.vue';
// import companySectionMixin from '../../mixins/companySectionMixin';
import { mapGetters } from 'vuex';

export default {
  name: 'AssignTruckDialog',
  mixins: [
    bookedLoadMixin,
    planningLoadGroupsMixin,
    // companySectionMixin,
  ],
  components: {
    BqaCertificateIcon,
    VSelectDriver,
  },
  props: {
    value: {
      type: Boolean,
    },
    load: {
      type: Object,
    },
    company: {
      type: Object,
      required: true,
    },
    outsideTrucks: {
      type: Boolean,
    },
  },
  data() {
    return {
      loadingBtn: false,
      openDialog: this.value,
      assignTruckCount: null,
      assignTruckDriverUserId: null,
      outsideTrucksVendorName: null,
      assignedDispatchContact: null,
      outsideTrucksNote: null,
      outsideTrucksRate: null,
      outsideTrucksPercentage: false,
      search: null,
    };
  },
  watch: {
    value() {
      this.openDialog = this.value;
      if (!this.openDialog) {
        this.resetForm();
      }
    },
    openDialog() {
      this.$emit('input', this.openDialog);
    },
  },
  computed: {
      // ...mapGetters('companies', {
      //   company: 'currentCompany',
      // }),
      canAssignDriverToDirectDispatchLoads() {
      return !this.assignTruckCount || this.assignTruckCount === 1;
    },
  },
  methods: {
    selectDriverChange(driverId) {
      this.assignTruckDriverUserId = driverId;
      this.assignTruckCount = this.assignTruckDriverUserId ? 1 : null;
    },
    async assignTrucks() {
      const isValid = await this.$validator.validateAll('modal');
      if (isValid) {
        const includeDriverId = this.assignTruckCount === 1 && this.assignTruckDriverUserId;
        try {
          this.$store.commit(`applications/${types.START_LOADING}`);
          const load = {
            companyId: this.load.loadGroup.details.listingCompany.id,
            truckCount: this.assignTruckCount,
          };

          if (includeDriverId) {
            load.driverUserId = this.assignTruckDriverUserId;
          }

          let apiResponse;
          if (this.outsideTrucks) {
            load.vendorName = this.outsideTrucksVendorName || this.assignedDispatchContact.name;
            load.dispatchContactId = this.assignedDispatchContact.id;
            load.notes = this.outsideTrucksNote;

            const isPercentage = this.outsideTrucksPercentage;
            load.amount = isPercentage ? null : this.outsideTrucksRate;
            load.percent = isPercentage ? this.outsideTrucksRate : null;
console.log('ASSIGNING LOAD', this.load.humanId, load)
            apiResponse = await API.planner.assignVendor({
              loadId: this.load.humanId,
              body: load,
            });
          } else {
            apiResponse = await API.planner.assignTrucks({
              loadId: this.load.humanId,
              body: load,
            });
          }

          this.$store.dispatch('applications/setSuccessNotification', {
            message: 'Assigned',
            timeout: 4000,
          });

          this.$emit('refresh', apiResponse);
          this.openDialog = false;

          // dispatch board resets when booked loads are refreshed. nothing to do there.
        } catch (error) {
          this.$store.dispatch('applications/setErrorNotification', {
            message: error.message,
          });
        } finally {
          this.$store.commit(`applications/${types.END_LOADING}`);
        }
      }
    },
    resetForm() {
      this.assignTruckDriverUserId = null;
      this.assignTruckCount = null;
      this.outsideTrucksVendorName = null;
      this.outsideTrucksPercentage = false;
      this.outsideTrucksNote = null;
      this.outsideTrucksRate = null;
    },
  },
};
</script>

<style scoped lang="css">
.checkbox-content {
  height: 30px;
}
</style>



// WEBPACK FOOTER //
// src/components/modals/AssignTruckDialog.vue